import { Stack, StackProps } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { LinkEntityIntegrationButton } from '@/components/form/baseInputs/LinkEntityIntegrationButton/LinkEntityIntegrationButton';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { GeneratingIndicator } from '@/modules/aiSuggestions/components/GeneratingIndicator';
import { INTEGRATION_EXCLUDED_ENTITY_TYPES } from '@/modules/assetProviderIntegrations/shared/constants';
import { useIsAssetProviderIntegrationEnabled } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { ViewHoldingsGroupButton } from '@/modules/estate/components/ViewHoldingsGroupButton/ViewHoldingsGroupButton';
import { EstateOnboardingEntitySuggestionsButton } from '@/modules/estate/estateAIOnboarding/EstateOnboardingEntitySuggestionsButton';
import {
  EstatePage_EntityFragment,
  EstatePage_UnacknowledgedAiSuggestionFragment,
  EstatePage_UnacknowledgedHoldingSuggestionFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { EntityStage } from '@/types/schema';

import { PrecreatedHoldingsGroupActions } from './PrecreatedHoldingsGroupActions/PrecreatedHoldingsGroupActions';

export interface HoldingsGroupActionsProps
  extends Omit<StackProps, 'direction'> {
  entity: EstatePage_EntityFragment;
  onAddHoldingClick: () => void;
  onLinkIntegrationClick: () => void;
  isHovered?: boolean;
  isGeneratingSuggestions?: boolean;
  // EstateOnboardingEntitySuggestionsButton props
  holdingsSuggestions?: EstatePage_UnacknowledgedHoldingSuggestionFragment[];
  /** Pre-computed V2 suggestions for this entity from the Estate context map. */
  entitySuggestionsV2?: EstatePage_UnacknowledgedAiSuggestionFragment[];
  onHoldingsSuggestionClick?: () => void;
  onEditEntitySectionSuggestionClick?: (section: EditEntitySection) => void;
}

export function HoldingsGroupActions({
  entity,
  onAddHoldingClick,
  onLinkIntegrationClick,
  isHovered = true,
  isGeneratingSuggestions = false,
  holdingsSuggestions = [],
  entitySuggestionsV2,
  onHoldingsSuggestionClick,
  onEditEntitySectionSuggestionClick,
  sx,
  ...stackProps
}: HoldingsGroupActionsProps) {
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isPreCreated = entity.stage === EntityStage.PreCreated;
  const entityType = getEntityTypeFromEntityKind(entity.kind);
  const { canConnectToIntegrations } = useIsAssetProviderIntegrationEnabled();
  const isIntegrationExcluded =
    INTEGRATION_EXCLUDED_ENTITY_TYPES.includes(entityType);

  const isVisible = isPopoverOpen || isHovered;

  if (isPreCreated) {
    return (
      <PrecreatedHoldingsGroupActions
        holdingsSuggestions={holdingsSuggestions}
        entity={entity}
        entitySuggestionsV2={entitySuggestionsV2}
        isIntegrationExcluded={isIntegrationExcluded}
        onAddHoldingClick={onAddHoldingClick}
        onLinkIntegrationClick={onLinkIntegrationClick}
        onHoldingsSuggestionClick={onHoldingsSuggestionClick}
        onEditEntitySectionSuggestionClick={onEditEntitySectionSuggestionClick}
      />
    );
  }

  return (
    <Stack direction="row" {...stackProps} spacing={1}>
      {isGeneratingSuggestions && canEdit && (
        <GeneratingIndicator dataTestId="estate-onboarding-entity-suggestions-button" />
      )}
      {isVisible &&
        canManageIntegrationData &&
        !isEmpty(canConnectToIntegrations) && (
          <LinkEntityIntegrationButton
            entity={entity}
            isIntegrationExcluded={isIntegrationExcluded}
            onLinkClick={onLinkIntegrationClick}
            isPopperMenuOpen={isPopoverOpen}
            onOpenPopperMenu={() => setIsPopoverOpen(true)}
            onClosePopperMenu={() => setIsPopoverOpen(false)}
          />
        )}
      {isVisible && canEdit && (
        <Tooltip title="Add holdings">
          <IconButton
            size="xs"
            variant="secondary"
            icon={PlusIcon}
            data-testid="add-holdings-button"
            onClick={(event) => {
              event.stopPropagation();
              onAddHoldingClick();
            }}
            ariaLabel="Add holdings"
          />
        </Tooltip>
      )}
      {canEdit && (
        <EstateOnboardingEntitySuggestionsButton
          holdingsSuggestions={holdingsSuggestions}
          entity={entity}
          isGenerating={false}
          entitySuggestionsV2={entitySuggestionsV2}
          onHoldingsSuggestionClick={onHoldingsSuggestionClick}
          onEditEntitySectionSuggestionClick={
            onEditEntitySectionSuggestionClick
          }
        />
      )}
      <ViewHoldingsGroupButton holdingsGroupId={entity.id} />
    </Stack>
  );
}
